import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, fromEvent, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class CsvValidatorService {
  private expectedHeaders: string[] = [];
  private maxRows = 1000;

  public readonly translate = inject(TranslateService);

  public validateCsv(
    file: File,
    expectedHeaders: string[],
    maxRows?: number
  ): Observable<string[]> {
    this.expectedHeaders = expectedHeaders;

    if (maxRows) {
      this.maxRows = maxRows;
    }

    const reader = new FileReader();
    const errors: string[] = [];

    const fileRead$ = fromEvent(reader, 'load').pipe(
      map((event) => (event.target as FileReader).result as string),
      switchMap((csvData) => this.parseCsvData(csvData, errors))
    );

    const fileError$ = fromEvent(reader, 'error').pipe(
      switchMap(() =>
        this.translate
          .get('csvValidation.readError')
          .pipe(switchMap((msg) => throwError(() => [msg])))
      )
    );

    reader.readAsText(file);

    return fileRead$.pipe(catchError(() => fileError$));
  }

  private parseCsvData(
    csvData: string,
    errors: string[]
  ): Observable<string[]> {
    const lines = csvData
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line);

    if (lines.length === 0) {
      return this.translate.get('csvValidation.fileEmptyOrInvalid').pipe(
        map((msg) => {
          errors.push(msg);
          return errors;
        })
      );
    }

    if (lines.length > this.maxRows + 1) {
      return this.translate
        .get('csvValidation.tooManyRows', { maxRows: this.maxRows })
        .pipe(
          map((msg) => {
            errors.push(msg);
            return errors;
          })
        );
    } else if (lines.length === 0) {
      return this.translate.get('csvValidation.noRows').pipe(
        map((msg) => {
          errors.push(msg);
          return errors;
        })
      );
    }

    const headers = lines[0].split(',').map((header) => header.trim());
    this.validateHeaders(headers, errors);

    return of(errors);
  }

  private validateHeaders(headers: string[], errors: string[]): void {
    this.expectedHeaders.forEach((expectedHeader) => {
      if (!headers.includes(expectedHeader)) {
        this.translate
          .get('csvValidation.missingHeader', { expected: expectedHeader })
          .subscribe((msg) => errors.push(msg));
      }
    });
  }
}
